import Vue from 'vue'

const isDesktop = (() => window.matchMedia("(hover: hover)").matches)();

export default {
  name: "BankIDComponent",

  data() {
    return {
      qrCodeSvg: null,
      bankIDAppOpened: !!this.$route.query.sessionId,
      sessionId: this.$route.query.sessionId,
      pendingLogin: false,
      showAuthenticationError: false,
      status: null,
      message: null,
      forbiddenMessage: null,
      isAndroidBrowser: false,
      device: null,
      abortController: new AbortController(),
      forSignIn: true,
      code: null,
      email: null,
      showLoader: false
    };
  },

  methods: {
    otherDevice(forSignIn = true) {
      this.isAndroidBrowser = false;
      if (this.abortController) {
        this.cancelRequest();
      }
      this.abortController = new AbortController();
      this.device = 'other';
      this.showLoader = true;

      return this.$store
        .dispatch("loginBankIDStep1", { loginType: 'otherDevice' })
        .then(async (res) => {
          await this.handleResponse(res, forSignIn);
          setTimeout(() => {
            this.showLoader = false;
          }, 500);
        })
        .catch((e) => {
          throw new Error("Something went wrong");
        });
    },

    sameDevice(forSignIn = true) {
      if (this.abortController) {
        this.cancelRequest();
      }
      this.abortController = new AbortController();
      this.device = 'same';
      this.showLoader = true;
      this.isAndroidBrowser = !this.isSafari() && !isDesktop;
      this.pendingLogin = true;

      return this.$store
        .dispatch("loginBankIDStep1", { loginType: 'sameDevice' })
        .then(async (res) => {
          setTimeout(() => {
            this.showLoader = false;
          }, 500);
          await this.handleResponse(res, forSignIn);
        })
        .catch(() => {
          throw new Error("Something went wrong");
        });
    },

    async handleResponse(res, forSignIn = true) {
      try {
        const { qrCodeSvg, sessionId } = res;
        const usingSameDevice = this.device === 'same';

        if (!usingSameDevice) {
          this.qrCodeSvg = qrCodeSvg;
        }

        this.sessionId = sessionId;

        if (usingSameDevice && !this.bankIDAppOpened && !this.abortController.signal.aborted) {
          await this.handleSameDevice({...res, forSignIn});
        } else if (!this.abortController.signal.aborted && !usingSameDevice) {
          this.pendingLogin = true;
          if (!forSignIn) {
            await this.startPollingWithoutSignin();
            return;
          }
          await this.startPolling();
        } else {
          this.cancelRequest();
        }
      } catch (err) {
        throw err;
      }
    },

    logError(err) {
      const keys = Object.keys(err);
      if (keys.length > 0) {
        keys.forEach(key => console.log(key, err[key]));
      } else {
        console.log(err);
      }
    },

    getAbortSignal() {
      if (!this.abortController) {
        this.abortController = new AbortController();
      }
      return this.abortController.signal;
    },

    async handleVisibility() {
      const isVisible = !document.hidden;
      if (isVisible) {
        if (!this.forSignIn) {
          await this.startPollingWithoutSignin();
          return;
        }
        await this.startPolling();
      } else {
        this.bankIDAppOpened = true;
        document.removeEventListener("visibilitychange", this.handleVisibility);
        this.cancelRequest();
      }
    },

    delay(ms) {
      return new Promise(res => setTimeout(res, ms));
    },

    async startPolling() {
      this.abortController = new AbortController();
      while (!this.abortController.signal.aborted) {
        try {
          const result = await this.$store.dispatch("loginBankIDStep2", {
            sessionId: this.sessionId,
            signal: this.getAbortSignal()
          });
          await this.handleResult(result);
          await this.delay(5000);
        } catch (err) {
          await this.handleError(err);
        }
      }
    },

    async startPollingWithoutSignin() {
      this.abortController = new AbortController();
      while (!this.abortController.signal.aborted) {
        try {
          const result = await this.$store.dispatch("getTokenForChangingEmail", {
            sessionId: this.sessionId,
            signal: this.getAbortSignal()
          });
          await this.handleResultWithoutSignin(result);
          await this.delay(5000);
        } catch (err) {
          await this.handleError(err);
        }
      }
    },

    async handleResultWithoutSignin(result) {
      const { pending, bankIdSuccess, message, hasError } = result;
      if (pending) {
        this.handlePendingRequest(result);
        if (hasError) {
          return Promise.reject({ noBankIdMessage: message });
        }
      } else if (result.code) {
        this.pendingLogin = false;
        this.cancelRequest();
        this.code = result.code;
        this.email = result.email;
        this.dataobj = result.data;
      }
    },

    async handleError(error) {
      if (error.noBankIdMessage) {
        this.status = error.noBankIdMessage;
        this.pendingLogin = false;
        this.cancelRequest();
        throw new Error("No BankID found");
      }

      if (error.response && error.response.status === 403) {
        await this.handleUnauthorized(error.response.data);
        this.pendingLogin = false;
        throw new Error("Forbidden");
      }

      if (error.bankIdErrorMessage) {
        this.pendingLogin = false;
        this.showAuthenticationError = true;

        setTimeout(() => {
          this.message = null;
          this.status = error.bankIdErrorMessage;
          this.$set(this, 'showAuthenticationError', true);
        }, 300);
        this.cancelRequest();
        throw new Error("User cancelled the request");
      }

      await this.handleUnauthorized(error.response?.data);
      this.pendingLogin = false;
      this.cancelRequest();
      throw error;
    },

    async handleResult(result) {
      const { pending, bankIdSuccess, message, hasError, primaryEmailIsInUse, emailNames } = result;
      if (pending) {
        this.handlePendingRequest(result);
        if (hasError) {
          return Promise.reject({ noBankIdMessage: message });
        }
      } else if (bankIdSuccess) {
        await this.$store.dispatch("setPrimaryEmailIsInUse", primaryEmailIsInUse);
        await this.$store.dispatch("setEmailNames", emailNames);

        this.pendingLogin = false;
        this.cancelRequest();
        this.$router.push({ name: 'Dashboard' });
      }
    },

    handlePendingRequest(result) {
      if (result.qrCodeSvg && this.device === 'other') {
        this.qrCodeSvg = result.qrCodeSvg;
      }
      if (result.message && !result.hasError) {
        this.message = result.message;
      }

      return result.hasError;
    },

    cancelRequest() {
      this.isAndroidBrowser = false;
      if (this.abortController) {
        this.abortController.abort("Cancelled the request");
      }
    },

    async handleUnauthorized(data) {
      this.forbiddenMessage = await this.$store.dispatch("handleUnauthorized", data);
    },

    addEventListeners() {
      window.addEventListener("blur", () => {
        this.cancelRequest();
        window.addEventListener("focus", this.handleVisibility, { once: true });
      }, { once: true });
    },

    async handleSameDevice({ autoStartToken, forSignIn = true }) {
      this.addEventListeners(); //here
      const routeParams = forSignIn ? { path: '/login/bankid', query: { sessionId: this.sessionId } } : {name: 'VerifyAndChangeEmailWithSession', params: { sessionId: this.sessionId }};
      const resolvedRoute = this.$router.resolve(routeParams).href;
      const redirectUrl = this.isAndroidBrowser ? "null" : location.origin + location.pathname + resolvedRoute;
      const bankIdProtocolLink = `bankid:///?autostarttoken=${autoStartToken}&redirect=${redirectUrl}`;
      const bankIdHttpsLink = `https://app.bankid.com/?autostarttoken=${autoStartToken}&redirect=${redirectUrl}`;

      window.location.href = isDesktop || this.isAndroidBrowser ? bankIdProtocolLink : bankIdHttpsLink;
      this.bankIDAppOpened = true;

      if (!forSignIn) {
        await this.startPollingWithoutSignin();
        return;
      }
      await this.startPolling();
    },

    isSafari() {
      const userAgent = window.navigator.userAgent;
      const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
      return isIOS && !/CriOS/.test(userAgent) && !/FxiOS/.test(userAgent);
    }
  }
};
